import { Dispatch, useEffect, useMemo, useState } from "react";
import { Channel } from "pusher-js";

import { useSocket } from "../../components";
import { RoleName } from "../../constants";
import {
  IAction,
  ISocketService,
  useDealIdSelector,
  useDispatch,
  useDealFormIdSelect,
  useUserIdSelector,
  useUserRoleSelector
} from "store";
import { ICommentParent, ICommentSocketData, IRole } from "store/types";
import { CommentsAction } from "store/actions";

const events: { [key: string]: string } = {
  create: "comments.created",
  update: "comments.updated",
  delete: "comments.deleted",
  subscriptionSucceed: "pusher:subscription_succeeded",
  subscriptionError: "pusher:subscription_error",
  error: "error"
};

interface IUseCommentsSocketHook {
  runSubscriptions: () => void;
}

export function useCommentsSocketHook(): IUseCommentsSocketHook {
  const [needSubscribe, setNeedSubscribe] = useState<boolean>(false);
  const [channels, setChannels] = useState<Channel[]>([]);

  const dispatch: Dispatch<IAction> = useDispatch();
  const socket: ISocketService = useSocket();
  const userId: number | undefined = useUserIdSelector();
  const dealId: number | undefined = useDealIdSelector();
  const formId: number | undefined = useDealFormIdSelect();
  const role: IRole | null = useUserRoleSelector();

  const channelNames: string[] = useMemo(() => {
    const names: string[] = [];

    // names.push("private-comments");

    if (dealId && formId && userId) {
      names.push(`private-comments-deal-${dealId}-form-${formId}`);

      names.push(`private-comments-me.${userId}`);

      if (role?.name === RoleName.Guest) {
        names.push(`private-comments-deal-${dealId}-form-${formId}-guest`);
      }

      if (role?.name === RoleName.Viewer) {
        names.push(`private-comments-deal-${dealId}-form-${formId}-viewer`); //subscribe to viewer channels
      }

      if (role?.name === RoleName.Analyst || role?.name === RoleName.Admin) {
        names.push(`private-comments-deal-${dealId}-form-${formId}-team`);
      }
    }
    return names;
  }, [dealId, formId, userId]);

  // Subscribe
  useEffect(() => {
    if (channelNames.length && needSubscribe) {
      setChannels(
        channelNames.map((name: string) => {
          return socket.subscribe(name);
        })
      );
      setNeedSubscribe(false);
    }
  }, [channelNames, socket, needSubscribe]);

  // Unsubscribe
  useEffect(() => {
    return () => {
      if (channelNames.length && channels.length && !needSubscribe) {
        channelNames.forEach((name: string) => {
          socket.unsubscribe(name);
          console.log("[Unsubscribe from channel]: ", name);
        });
        setNeedSubscribe(false);
        setChannels([]);
      }
    };
  }, [channelNames, channels, needSubscribe]);

  // Listen Events
  useEffect(() => {
    if (channels.length) {
      channels.forEach((channel: Channel) => {
        channel.bind(events.subscriptionSucceed, () => {
          console.log(`[${channel.name}]: connection succeed`);
        });

        channel.bind(events.subscriptionError, (error: any) => {
          console.log(`[${channel.name}]: connection error`, error);
        });

        channel.bind(events.error, (error: any) => {
          console.log("[Comments error]: ", error);
        });

        channel.bind(events.create, (data: ICommentSocketData) => {
          const { comment } = data;
          dispatch({
            type: CommentsAction.CreateCommentSocket,
            payload: comment
          });
          console.log("[Posted new comment] : ", comment);
        });

        channel.bind(events.update, (data: { comment: ICommentParent }) => {
          const { comment } = data;
          dispatch({
            type: CommentsAction.UpdateCommentSocket,
            payload: comment
          });
          console.log("[Update comment] : ", comment);
        });

        channel.bind(events.delete, (data: ICommentSocketData) => {
          const { comment } = data;
          dispatch({
            type: CommentsAction.DeleteCommentSocket,
            payload: comment
          });
          console.log("[Delete comment] : ", comment);
        });
      });
    }
  }, [channels, dispatch]);
  return {
    runSubscriptions: () => setNeedSubscribe(true)
  };
}
